<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { loadFactionsForDomain } from '@api/roster';
import type { RosterType } from '@interfaces/roster_type';
import type { StructureType } from '@interfaces/structure_type';
import type { FactionType } from '@interfaces/faction_type';
import { useLocalizedNamesStore } from '../../../stores/localizedNames';

export default defineComponent({
  name: 'SelectFaction',
  props: {
    roster: {
      type: Object as PropType<RosterType>,
      required: true
    },
    structure: {
      type: Object as PropType<StructureType>,
      required: true
    }
  },
  data() {
    return {
      factionsLoading: false,
    };
  },
  watch: {
    locale() {
      this.ensureFactionNamesLoaded();
    },
    'structure.domain': {
      handler() {
        this.loadFactionsIfNeeded();
        this.ensureFactionNamesLoaded();
      },
      deep: false,
      immediate: true
    },
  },
  mounted() {
    this.loadFactionsIfNeeded();
    this.ensureFactionNamesLoaded();
  },
  methods: {
    factionHasHeroes(faction: FactionType): boolean {
      return faction.has_heroes === true || (faction.available_heroes?.length ?? 0) > 0;
    },
    async loadFactionsIfNeeded(): Promise<void> {
      const domain = this.structure.domain;
      if (!domain) return;
      if (domain.available_factions && domain.available_factions.length > 0) return;

      this.factionsLoading = true;
      try {
        const factions = await loadFactionsForDomain(
          domain.id ?? null,
          domain.tech_name ?? undefined
        );
        this.structure.domain = { ...domain, available_factions: factions };
      } finally {
        this.factionsLoading = false;
      }
    },
    select(faction: FactionType): void {
      if (!this.factionHasHeroes(faction)) return;

      if (this.structure) {
        this.structure.faction = faction;
        this.$emit('select-step', 'selectHero');
      }
    },
    factionName(faction: FactionType): string {
      const id = faction.id ?? undefined;
      const techName = faction.tech_name ?? undefined;
      return (
        (id ? this.localizedNamesStore.getName('faction', undefined, id) : null) ||
        (techName ? this.localizedNamesStore.getName('faction', techName) : null) ||
        faction.name ||
        ''
      );
    },
    async ensureFactionNamesLoaded(): Promise<void> {
      const factions = this.structure.domain?.available_factions;
      if (!factions?.length) return;

      const items = factions
        .filter((f) => f.id !== null || f.tech_name)
        .map((f) => ({ type: 'faction' as const, id: f.id ?? undefined, tech_name: f.tech_name ?? undefined }));

      await this.localizedNamesStore.ensureNamesLoaded(items, this.locale);
    },
  },
  setup() {
    const i18n = useI18n();
    const localizedNamesStore = useLocalizedNamesStore();
    return {
      t: i18n.t,
      locale: i18n.locale,
      localizedNamesStore,
    };
  },
});
</script>

<template>
  <div class="roster-builder">
    <h1 class="roaster-title">{{ t('select_faction.title') }}</h1>

    <div v-if="factionsLoading" class="text-center">{{ t('select_domain.loading') }}...</div>
    <template v-else>
      <div v-for="faction in structure.domain?.available_factions?.sort((a, b) => (a.weight ?? 1) - (b.weight ?? 1))"
           :key="faction.id ?? faction.tech_name"
           class="element faction w-20"
           :class="{
             'active': structure?.faction?.id === faction.id || structure?.faction?.tech_name === faction.tech_name,
             'disabled': !factionHasHeroes(faction)
           }"
           :disabled="!factionHasHeroes(faction)"
           @click="select(faction)">
        <div class="faction-image">
          <img v-image-fallback="faction.image_path"
               class="img-fluid img-domain"
               :alt="factionName(faction) || 'Faction image'"/>
        </div>
        <h2 class="text-center">{{ factionName(faction) }}</h2>
      </div>
    </template>
  </div>
</template>
